import Phaser from 'phaser';
import { GameState } from '../game/GameState';
import { SceneKeys } from '../game/Constants';
import { AdvisorManager, advisorRoles } from '../systems/AdvisorManager';
import { AssetRegistry } from '../systems/AssetRegistry';
import { DataRegistry } from '../systems/DataRegistry';
import { DynastyManager, successionLaws } from '../systems/DynastyManager';
import { LawManager, laws } from '../systems/LawManager';
import type { CharacterState, CourtEstateState, SuccessionLaw } from '../types/game';
import { ensureCourtEventTexture, ensureCourtPortraitTexture } from '../ui/CourtProceduralArt';
import { VisualTheme } from '../ui/VisualTheme';
import { createTextButton } from '../ui/components/UIButton';
import { addBackButton, drawPanel, drawScreenBackground } from '../ui/components/SceneChrome';

const TABS = ['Dynasty', 'Family', 'Estates', 'Council', 'Chronicle'] as const;
type CourtTab = typeof TABS[number];

export class CourtScene extends Phaser.Scene {
  private activeTab: CourtTab = 'Dynasty';
  private selectedIncidentId?: string;

  constructor() {
    super(SceneKeys.Court);
  }

  create() {
    DynastyManager.instance.ensureCampaignInitialized();
    this.render();
  }

  private render() {
    this.children.removeAll();
    DynastyManager.instance.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const factionId = campaign.playerFactionId;
    const factionData = DataRegistry.instance.factions[factionId];
    const court = DynastyManager.instance.courtForFaction(factionId);
    const backdrop = ensureCourtEventTexture(this, court.regency ? 'regency' : 'coronation');

    drawScreenBackground(this, 'Court and Dynasty', `${factionData?.name ?? factionId}  •  House ${court.house}`);
    this.add.image(800, 500, backdrop).setDisplaySize(1600, 900).setAlpha(0.2).setDepth(-2);
    this.add.rectangle(800, 500, 1600, 900, VisualTheme.colors.ink, 0.7).setDepth(-1);
    addBackButton(this);
    this.drawStatusBar(factionId);
    this.drawTabs();

    if (this.activeTab === 'Dynasty') this.drawDynastyTab(factionId);
    else if (this.activeTab === 'Family') this.drawFamilyTab(factionId);
    else if (this.activeTab === 'Estates') this.drawEstatesTab(factionId);
    else if (this.activeTab === 'Council') this.drawCouncilTab(factionId);
    else this.drawChronicleTab(factionId);

    const exposed = window as unknown as { render_game_to_text?: () => string; advanceTime?: (ms: number) => void };
    exposed.render_game_to_text = () => JSON.stringify({
      mode: 'court',
      tab: this.activeTab,
      factionId,
      house: court.house,
      ruler: court.ruler?.name,
      heir: court.heir?.name,
      consort: court.consort?.name,
      regency: court.regency,
      successionLaw: court.successionLaw,
      successionClarity: court.successionClarity,
      characters: DynastyManager.instance.courtiersForFaction(factionId).map((character) => ({ id: character.id, name: character.name, role: character.role, age: character.age, health: character.health, loyalty: character.loyalty, influence: character.influence })),
      estates: DynastyManager.instance.estatesForFaction(factionId).map((estate) => ({ id: estate.id, approval: estate.approval, influence: estate.influence, power: estate.power })),
      incidents: DynastyManager.instance.incidentsForFaction(factionId).map((incident) => ({ id: incident.id, type: incident.type, severity: incident.severity, title: incident.title })),
    });
    exposed.advanceTime = () => undefined;
  }

  private drawStatusBar(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const court = DynastyManager.instance.courtForFaction(factionId);
    const incidents = DynastyManager.instance.incidentsForFaction(factionId);
    const metrics = [
      ['Legitimacy', faction.legitimacy, VisualTheme.text.title],
      ['Prestige', faction.prestige, VisualTheme.text.success],
      ['Succession', court.successionClarity, court.successionClarity < 35 ? VisualTheme.text.danger : VisualTheme.text.accent],
      ['Living dynasty', DynastyManager.instance.familyForFaction(factionId).filter((character) => character.alive).length, VisualTheme.text.body],
      ['Court crises', incidents.length, incidents.length ? VisualTheme.text.danger : VisualTheme.text.success],
      ['Treasury', faction.treasury, VisualTheme.text.accent],
    ];
    metrics.forEach(([label, value, color], index) => {
      const x = 250 + index * 220;
      this.add.rectangle(x, 95, 198, 58, VisualTheme.colors.night, 0.96).setStrokeStyle(1, VisualTheme.colors.gold, 0.42);
      this.add.text(x - 84, 78, String(label).toUpperCase(), { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: VisualTheme.text.muted });
      this.add.text(x - 84, 94, String(Math.round(Number(value))), { fontFamily: VisualTheme.fonts.display, fontSize: '23px', color: String(color) });
    });
  }

  private drawTabs() {
    TABS.forEach((tab, index) => {
      const active = this.activeTab === tab;
      const button = createTextButton(this, {
        label: tab,
        x: 260 + index * 260,
        y: 153,
        width: 232,
        height: 40,
        fontSize: 15,
        onClick: () => { this.activeTab = tab; this.render(); },
      });
      if (active) button.setScale(1.03);
    });
  }

  private drawDynastyTab(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const court = DynastyManager.instance.courtForFaction(factionId);
    drawPanel(this, 475, 485, 850, 590, court.regency ? 'The Regency Council' : 'The Reigning House');

    const portraits: Array<{ title: string; character?: CharacterState }> = [
      { title: court.regency ? 'Young Ruler' : 'Ruler', character: court.ruler },
      { title: 'Recognized Heir', character: court.heir },
      { title: court.regency ? 'Regent' : 'Consort', character: court.regency ? court.regent : court.consort },
    ];
    portraits.forEach((entry, index) => this.drawPortraitCard(150 + index * 275, 250, 224, 300, entry.title, entry.character, true));

    this.add.text(84, 438, `${court.regency ? 'REGENCY IN FORCE' : 'THE CROWN'}  •  ${successionLaws.find((law) => law.id === court.successionLaw)?.label ?? court.successionLaw}`, {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '20px',
      color: court.regency ? VisualTheme.text.danger : VisualTheme.text.title,
    });
    this.add.text(84, 470, court.regency
      ? 'A minor ruler depends on the loyalty and ambition of the regent. Legitimacy will erode if the regency becomes unstable.'
      : 'Marriage, health, claims and estate approval determine whether succession strengthens the realm or tears it apart.', {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '14px',
      color: VisualTheme.text.body,
      wordWrap: { width: 765 },
    });

    createTextButton(this, {
      label: 'Hold Great Court · 45g', x: 240, y: 548, width: 280, height: 44, fontSize: 15,
      onClick: () => { DynastyManager.instance.holdCourt(factionId); this.render(); },
    });
    createTextButton(this, {
      label: 'Open Family Tree', x: 540, y: 548, width: 280, height: 44, fontSize: 15,
      onClick: () => { this.activeTab = 'Family'; this.render(); },
    });

    drawPanel(this, 1205, 485, 570, 590, 'Succession Law');
    this.add.text(950, 216, 'Changing the law costs 75 gold and 8 legitimacy. The nobility will resist sudden reforms.', {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '14px',
      color: VisualTheme.text.muted,
      wordWrap: { width: 500 },
    });
    successionLaws.forEach((law, index) => {
      const y = 290 + index * 74;
      const active = faction.dynasty?.successionLaw === law.id;
      this.add.rectangle(1205, y, 505, 57, active ? VisualTheme.colors.jade : VisualTheme.colors.slate, active ? 0.72 : 0.8).setStrokeStyle(2, active ? VisualTheme.colors.goldBright : VisualTheme.colors.gold, active ? 0.95 : 0.35);
      this.add.text(970, y - 13, law.label, { fontFamily: VisualTheme.fonts.display, fontSize: '17px', color: active ? VisualTheme.text.title : VisualTheme.text.body });
      this.add.text(970, y + 11, this.lawDescription(law.id), { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: VisualTheme.text.muted, wordWrap: { width: 320 } });
      if (!active) createTextButton(this, { label: 'Reform', x: 1415, y, width: 120, height: 34, fontSize: 12, onClick: () => { DynastyManager.instance.changeSuccessionLaw(factionId, law.id); this.render(); } });
    });
  }

  private drawFamilyTab(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const court = DynastyManager.instance.courtForFaction(factionId);
    const family = DynastyManager.instance.familyForFaction(factionId);
    drawPanel(this, 800, 495, 1480, 610, `Family Tree · House ${court.house}`);

    const ruler = court.ruler;
    const consort = court.consort;
    if (ruler && consort) {
      const lines = this.add.graphics();
      lines.lineStyle(4, VisualTheme.colors.gold, 0.75).lineBetween(540, 329, 1060, 329).lineBetween(800, 329, 800, 430);
      const children = family.filter((character) => character.parentIds.includes(ruler.id) || character.parentIds.includes(consort.id));
      if (children.length) {
        lines.lineBetween(270, 430, 1330, 430);
        children.slice(0, 5).forEach((_, index) => lines.lineBetween(270 + index * 265, 430, 270 + index * 265, 475));
      }
      this.drawPortraitCard(420, 220, 220, 250, 'Ruler', ruler, false);
      this.drawPortraitCard(1180, 220, 220, 250, 'Consort', consort, false);
      children.slice(0, 5).forEach((child, index) => this.drawPortraitCard(180 + index * 265, 500, 205, 245, child.id === court.heir?.id ? 'Heir' : 'Dynast', child, false));
    } else {
      family.slice(0, 10).forEach((character, index) => this.drawPortraitCard(160 + (index % 5) * 300, 230 + Math.floor(index / 5) * 290, 225, 250, character.role, character, false));
    }

    this.add.text(80, 764, `${family.filter((character) => character.alive).length} living members  •  ${family.filter((character) => !character.alive).length} remembered dead  •  ${family.filter((character) => character.claimStrength >= 45 && character.alive).length} credible claimants`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '15px',
      color: VisualTheme.text.accent,
    });
    const designated = family.find((character) => character.id === campaign.selectedCharacterId);
    if (factionId === campaign.playerFactionId && designated && court.successionLaw === 'designated_heir' && designated.alive && designated.id !== court.ruler?.id) {
      createTextButton(this, { label: `Designate ${designated.name} as heir`, x: 1240, y: 780, width: 420, height: 42, fontSize: 14, onClick: () => { DynastyManager.instance.designateHeir(factionId, designated.id); this.render(); } });
    }
  }

  private drawEstatesTab(factionId: string) {
    const estates = DynastyManager.instance.estatesForFaction(factionId);
    const demands = DynastyManager.instance.demandsForFaction(factionId);
    drawPanel(this, 500, 500, 900, 610, 'Power Blocs of the Realm');
    estates.forEach((estate, index) => this.drawEstateCard(105, 222 + index * 104, estate));

    drawPanel(this, 1240, 500, 560, 610, 'Petitions and Demands');
    if (!demands.length) {
      this.add.text(990, 252, 'No estate has placed a formal demand before the throne. Their approval and power still shape the risk of coups and rebellions.', {
        fontFamily: VisualTheme.fonts.body, fontSize: '16px', color: VisualTheme.text.body, wordWrap: { width: 490 }, lineSpacing: 7,
      });
    }
    demands.slice(0, 4).forEach((demand, index) => {
      const y = 238 + index * 137;
      const estate = estates.find((candidate) => candidate.id === demand.estateId);
      this.add.rectangle(1240, y + 45, 500, 123, VisualTheme.colors.slate, 0.9).setStrokeStyle(2, VisualTheme.colors.gold, 0.4);
      this.add.text(1010, y, demand.title, { fontFamily: VisualTheme.fonts.display, fontSize: '18px', color: VisualTheme.text.title });
      this.add.text(1010, y + 27, `${estate?.name ?? demand.estateId}  •  answer in ${Math.max(0, demand.dueTurn - GameState.instance.requireCampaign().turn)} months\n${demand.description}`, {
        fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.body, wordWrap: { width: 450 }, lineSpacing: 3,
      });
      createTextButton(this, { label: `Accept · ${demand.treasuryCost ?? 0}g`, x: 1120, y: y + 91, width: 190, height: 31, fontSize: 11, onClick: () => { DynastyManager.instance.respondToDemand(factionId, demand.id, true); this.render(); } });
      createTextButton(this, { label: 'Refuse', x: 1360, y: y + 91, width: 190, height: 31, fontSize: 11, onClick: () => { DynastyManager.instance.respondToDemand(factionId, demand.id, false); this.render(); } });
    });
  }

  private drawCouncilTab(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const advisors = AdvisorManager.instance.advisorsForFaction(factionId);
    const courtiers = DynastyManager.instance.courtiersForFaction(factionId);
    drawPanel(this, 800, 500, 1480, 610, 'Council, Commanders and Great Officers');
    this.add.text(85, 205, `Council capacity ${Object.values(faction.advisorSlots).filter(Boolean).length}/${AdvisorManager.instance.capacity(factionId)}  •  office holders are persistent characters and can die, defect or become regents`, {
      fontFamily: VisualTheme.fonts.body, fontSize: '15px', color: VisualTheme.text.accent,
    });

    advisorRoles.forEach((role, index) => {
      const x = 165 + (index % 4) * 370;
      const y = 275 + Math.floor(index / 4) * 270;
      const advisor = advisors.find((candidate) => candidate.role === role);
      const character = advisor ? courtiers.find((candidate) => candidate.advisorRole === advisor.role || candidate.name === advisor.name) : undefined;
      this.add.rectangle(x, y, 330, 230, VisualTheme.colors.night, 0.94).setStrokeStyle(2, VisualTheme.colors.gold, 0.55);
      if (character) this.drawPortrait(character, x - 104, y - 22, 102, 128);
      this.add.text(x - 35, y - 92, role, { fontFamily: VisualTheme.fonts.display, fontSize: '18px', color: VisualTheme.text.title });
      this.add.text(x - 35, y - 59, advisor ? `${advisor.name}\nSkill ${advisor.skill} · ${advisor.trait ?? 'Courtier'}\nLoyalty ${character?.loyalty ?? '—'} · Influence ${character?.influence ?? '—'}` : 'Vacant office', {
        fontFamily: VisualTheme.fonts.body, fontSize: '13px', color: VisualTheme.text.body, lineSpacing: 4,
      });
      if (advisor) createTextButton(this, {
        label: faction.advisorSlots[role] === advisor.id ? 'Assigned' : 'Appoint', x: x + 52, y: y + 72, width: 170, height: 34, fontSize: 12,
        onClick: () => { AdvisorManager.instance.assignAdvisor(factionId, role, advisor.id); this.render(); },
      });
    });

    this.add.text(85, 760, `Active laws: ${faction.activeLaws.map((id) => laws.find((law) => law.id === id)?.name ?? id).join(', ')}`, {
      fontFamily: VisualTheme.fonts.body, fontSize: '14px', color: VisualTheme.text.muted, wordWrap: { width: 900 },
    });
    const available = LawManager.instance.availableLaws(factionId)[0];
    if (available) createTextButton(this, { label: `Enact ${available.name} · ${available.cost}g`, x: 1280, y: 777, width: 420, height: 40, fontSize: 13, onClick: () => { LawManager.instance.enactLaw(factionId, available.id); this.render(); } });
  }

  private drawChronicleTab(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const incidents = DynastyManager.instance.incidentsForFaction(factionId);
    const history = campaign.dynastyHistory.filter((entry) => entry.factionId === factionId);
    const incident = incidents.find((entry) => entry.id === this.selectedIncidentId) ?? incidents[0];
    if (incident) this.selectedIncidentId = incident.id;

    drawPanel(this, 505, 500, 910, 610, incident ? incident.title : 'The Realm Is at Peace');
    if (incident) {
      const keyFromPack = AssetRegistry.instance.image(incident.imageAsset);
      const key = this.textures.exists(keyFromPack) && keyFromPack !== 'fallback.panel' ? keyFromPack : ensureCourtEventTexture(this, incident.type);
      this.add.image(505, 355, key).setDisplaySize(840, 360).setCrop(0, 0, 1200, 675);
      this.add.rectangle(505, 535, 840, 94, VisualTheme.colors.ink, 0.88);
      this.add.text(105, 497, `${incident.type.replaceAll('_', ' ').toUpperCase()}  •  SEVERITY ${incident.severity}`, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: incident.severity >= 4 ? VisualTheme.text.danger : VisualTheme.text.accent });
      this.add.text(105, 522, incident.description, { fontFamily: VisualTheme.fonts.body, fontSize: '15px', color: VisualTheme.text.body, wordWrap: { width: 780 } });
      createTextButton(this, { label: 'Conciliate', x: 330, y: 632, width: 300, height: 42, fontSize: 14, onClick: () => { DynastyManager.instance.resolveIncident(factionId, incident.id, true); this.render(); } });
      createTextButton(this, { label: 'Assert Authority', x: 680, y: 632, width: 300, height: 42, fontSize: 14, onClick: () => { DynastyManager.instance.resolveIncident(factionId, incident.id, false); this.render(); } });
    } else {
      const key = ensureCourtEventTexture(this, 'coronation');
      this.add.image(505, 380, key).setDisplaySize(840, 470).setAlpha(0.82);
      this.add.text(505, 665, 'No unresolved court crisis currently threatens the crown.', { fontFamily: VisualTheme.fonts.display, fontSize: '20px', color: VisualTheme.text.success }).setOrigin(0.5);
    }

    drawPanel(this, 1245, 500, 550, 610, 'Dynastic Chronicle');
    incidents.slice(0, 3).forEach((entry, index) => {
      const y = 230 + index * 78;
      const selected = entry.id === incident?.id;
      const box = this.add.rectangle(1245, y, 500, 66, selected ? VisualTheme.colors.crimson : VisualTheme.colors.slate, selected ? 0.72 : 0.82).setStrokeStyle(2, selected ? VisualTheme.colors.goldBright : VisualTheme.colors.gold, selected ? 0.95 : 0.35).setInteractive();
      if (box.input) box.input.cursor = 'pointer';
      box.on('pointerdown', () => { this.selectedIncidentId = entry.id; this.render(); });
      this.add.text(1010, y - 20, entry.title, { fontFamily: VisualTheme.fonts.display, fontSize: '16px', color: VisualTheme.text.title });
      this.add.text(1010, y + 6, `${entry.type.replaceAll('_', ' ')} · severity ${entry.severity}`, { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: VisualTheme.text.muted });
    });
    const startY = incidents.length ? 485 : 240;
    history.slice(0, incidents.length ? 7 : 12).forEach((entry, index) => {
      const y = startY + index * 42;
      this.add.circle(1018, y + 7, 4, VisualTheme.colors.goldBright, 0.9);
      this.add.text(1032, y - 5, `${entry.year}.${entry.month}  ${entry.title}`, { fontFamily: VisualTheme.fonts.display, fontSize: '14px', color: VisualTheme.text.body });
      this.add.text(1032, y + 15, entry.description, { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: VisualTheme.text.muted, wordWrap: { width: 430 } });
    });
  }

  private drawPortraitCard(x: number, y: number, width: number, height: number, title: string, character: CharacterState | undefined, large: boolean) {
    const panel = this.add.rectangle(x, y, width, height, VisualTheme.colors.night, 0.96).setStrokeStyle(2, character?.alive === false ? VisualTheme.colors.slate : VisualTheme.colors.gold, character?.alive === false ? 0.6 : 0.85);
    panel.setInteractive();
    if (panel.input) panel.input.cursor = character ? 'pointer' : 'default';
    if (character) panel.on('pointerdown', () => { GameState.instance.requireCampaign().selectedCharacterId = character.id; if (!large) this.render(); });
    this.add.text(x, y - height / 2 + 15, title.toUpperCase(), { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: VisualTheme.text.accent }).setOrigin(0.5);
    if (!character) {
      this.add.text(x, y, 'No recognized office holder', { fontFamily: VisualTheme.fonts.body, fontSize: '13px', color: VisualTheme.text.muted, wordWrap: { width: width - 30 }, align: 'center' }).setOrigin(0.5);
      return;
    }
    this.drawPortrait(character, x, y - (large ? 35 : 25), width - 30, large ? 190 : 145);
    this.add.text(x, y + height / 2 - (large ? 68 : 61), character.name, { fontFamily: VisualTheme.fonts.display, fontSize: large ? '18px' : '15px', color: character.alive ? VisualTheme.text.title : VisualTheme.text.muted, wordWrap: { width: width - 24 }, align: 'center' }).setOrigin(0.5);
    this.add.text(x, y + height / 2 - (large ? 38 : 34), `${character.alive ? `Age ${character.age}` : `Died ${character.deathYear ?? ''}`} · Health ${Math.round(character.health)}\n${character.traits.slice(0, 2).join(' · ')}`, { fontFamily: VisualTheme.fonts.body, fontSize: large ? '11px' : '10px', color: VisualTheme.text.muted, align: 'center' }).setOrigin(0.5);
  }

  private drawPortrait(character: CharacterState, x: number, y: number, width: number, height: number) {
    const requested = AssetRegistry.instance.image(character.portraitAsset);
    const key = this.textures.exists(requested) && requested !== 'fallback.panel' ? requested : ensureCourtPortraitTexture(this, character.factionId, character.role);
    const image = this.add.image(x, y, key).setDisplaySize(width, height);
    if (!character.alive) image.setTint(0x7c8791).setAlpha(0.72);
    else if (character.health < 30) image.setTint(0xb8a6a2);
    this.add.rectangle(x, y, width, height, 0x000000, 0).setStrokeStyle(2, character.alive ? VisualTheme.colors.goldBright : VisualTheme.colors.slate, 0.85);
  }

  private drawEstateCard(x: number, y: number, estate: CourtEstateState) {
    this.add.rectangle(x + 360, y, 790, 88, VisualTheme.colors.slate, 0.9).setStrokeStyle(2, estate.approval < 25 ? VisualTheme.colors.crimson : VisualTheme.colors.gold, estate.approval < 25 ? 0.95 : 0.4);
    this.add.text(x, y - 29, estate.name, { fontFamily: VisualTheme.fonts.display, fontSize: '19px', color: VisualTheme.text.title });
    this.add.text(x, y - 2, `Approval ${Math.round(estate.approval)}  •  Influence ${Math.round(estate.influence)}  •  Power ${Math.round(estate.power)}`, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.body });
    this.drawBar(x, y + 28, 220, estate.approval, estate.approval < 25 ? VisualTheme.colors.crimson : VisualTheme.colors.jadeBright);
    this.drawBar(x + 255, y + 28, 220, estate.influence, VisualTheme.colors.goldBright);
    this.drawBar(x + 510, y + 28, 220, estate.power, estate.power > 60 ? VisualTheme.colors.crimson : VisualTheme.colors.oceanLight);
  }

  private drawBar(x: number, y: number, width: number, value: number, fill: number) {
    this.add.rectangle(x + width / 2, y, width, 8, VisualTheme.colors.ink, 0.9).setStrokeStyle(1, VisualTheme.colors.gold, 0.25);
    this.add.rectangle(x + Math.max(1, width * value / 100) / 2, y, Math.max(1, width * value / 100), 6, fill, 0.95);
  }

  private lawDescription(law: SuccessionLaw) {
    if (law === 'agnatic_primogeniture') return 'The eldest eligible male dynast inherits.';
    if (law === 'absolute_primogeniture') return 'The strongest senior child inherits regardless of gender.';
    if (law === 'seniority') return 'The oldest living dynast takes the throne.';
    if (law === 'elective_court') return 'Influence, loyalty and claims decide the court election.';
    return 'The ruler may publicly nominate any eligible dynast.';
  }
}
